import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// our components
import NorowsData from './NorowsData'
import Notification from './Notification'
import Carrier from './Carrier'
import OrderStatus from './OrderStatus'
import Customer from './Customer'
import CarrierTracking from './CarrierTracking'
import ShipmentRate from './ShipmentRate'
import Date from './Date'
import WidthMaximize from './WidthMaximize'
import TrackingDrawer from '../../pages/General/TrackingDrawer';
import OrderDrawer from '../General/OrderDrawer';

// mui components
import {
    FormControl,
    Pagination,
    Card,
    IconButton,
    Drawer,
    CardHeader,
    Paper,
    LinearProgress
} from "@mui/material";
import { styled } from '@mui/material/styles';
import {
    DataGrid,
    GridToolbarContainer
} from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';

// Redux actions
import { fetchOrderDetail } from '../../pages/OrderDetail/orderdetailsSlice';
import { fetchCustomerDetails } from '../Reports/Deliveries/trackingEventsSlice';
import {
    fetchUnclosedShipments,
    resetDeliveriessNotifications
} from '../Reports/Deliveries/deliveriesmysqlSlice';

const UnclosedShipmentDrawer = ({ visible, unclosedShipmentsOnClose }) => {
    const dispatch = useDispatch();
    const deliveries = useSelector(state => state.deliveriesmysql);
    const profile = useSelector(state => state.auth);
    
    // UPDATED: Pointing to unclosedShipmentData meta
    const [meta, setMeta] = useState(deliveries.unclosedShipmentData.meta);
    const [trackingDrawerVisible, setTrackingDrawerVisible] = useState(false);
    const [orderDrawerVisible, setOrderDrawerVisible] = useState(false);
    const [isMultiPiece, setIsMultiPiece] = useState(false);
    const [drawerWidth, setDrawerWidth] = useState("80%");
    
    const ondrawerclose = () => {
        dispatch(resetDeliveriessNotifications());
        unclosedShipmentsOnClose(false);
    }

    let columns = [
        {
            field: 'order_id',
            headerName: 'Order',
            headerClassName: 'super-app-theme--header',
            minWidth: 180,
            sortable: false,
            flex: 1,
            renderCell: (params) => {
                return <span style={{ color: 'rgb(24, 144, 255)', cursor: 'pointer' }} onClick={e => { orderCallback(params.row.order_id) }}>{params.row.order_id}</span>
            }
        },
        {
            field: 'master_tracking_number',
            headerName: 'Tracking Number',
            minWidth: 180,
            sortable: false,
            flex: 1,
            renderCell: (params) => {
                let tracking_number = params.row.master_tracking_number || "";
                let carrier_name = params.row.label?.carrier_name || "";
                let message = (params.row.manifest && params.row.manifest.is_closed === false)
                    ? "This shipment manifest is not yet closed" : null;
                let tracking = { "tracking_number": tracking_number, "carrier_name": carrier_name };
                return (
                    <CarrierTracking trackingItems={tracking} dispatchCallback={trackingCallback} carrierImageWidth="60px" message={message} />
                )
            }
        },
        {
            field: 'status',
            headerName: 'Shipment Status',
            minWidth: 180,
            sortable: false,
            flex: 1,
            renderCell: (params) => {
                return <OrderStatus orderStatus={params.row.status} />
            }
        },
        {
            field: 'customer_first_name',
            headerName: 'Customer',
            minWidth: 170,
            sortable: false,
            flex: 1,
            renderCell: (params) => {
                let receiver = params.row.address?.receiver;
                let name = "";
                if (receiver) {
                    name = receiver.first_name || "";
                    if (receiver.last_name) {
                        name = name + " " + receiver.last_name;
                    }
                }
                return <Customer name={name} />
            }
        },
        {
            field: 'shipment_date',
            headerName: 'Shipment Date',
            minWidth: 180,
            flex: 1,
            renderCell: (params) => {
                if (params.row && params.row.created_at) {
                    return <Date date={params.row.created_at} />
                } else {
                    return "-"
                }
            }
        },
        {
            field: 'rate_total_charges',
            headerName: 'Rate',
            sortable: false,
            flex: 1,
            minWidth: 100,
            renderCell: (params) => {
                let voided = params.row.label?.is_voided ? true : false;
                return <ShipmentRate
                    billed_rate={params.row.rate?.breakdown?.total_charge}
                    api_rate={params.row.rate?.customer_price}
                    is_multi_piece={params.row.is_multi_piece}
                    is_voided={voided}
                />
            }
        },
    ];

    // UPDATED: Sync internal meta with Redux meta if needed
    useEffect(() => {
        if (visible) {
            setMeta(deliveries.unclosedShipmentData.meta);
        }
    }, [visible]);

    // UPDATED: Fetching using the specific unclosed shipment action
    useEffect(() => {
        if (visible) {
            dispatch(fetchUnclosedShipments({
                ...meta,
                unclosed_shipment: true
            }));
        }
    }, [dispatch, meta, visible]);

    const orderCallback = (item) => {
        dispatch(fetchOrderDetail(item));
        setOrderDrawerVisible(true);
    }

    const orderOnClose = () => {
        setOrderDrawerVisible(false);
    }

    const trackingCallback = (carriername, trackingNumber) => {
        let apikey = (profile && profile.profile && profile.profile.api_token) ? profile.profile.api_token : "";
        const data = { 'tracking_number': trackingNumber };
        if(trackingNumber.charAt(0) === 'M'){
          setIsMultiPiece(true);
        }
        dispatch(fetchCustomerDetails(data, apikey));
        setTrackingDrawerVisible(true);
    }

    const onSortChange = (currentSorting) => {
        setMeta({ ...meta, sorting: JSON.stringify(currentSorting) });
    }

    const onChangePerPage = (event) => {
        setMeta({ ...meta, page: 1, per_page: event.target.value });
    }

    const onChangePagination = (event, page) => {
        setMeta({ ...meta, page: page });
    }

    function CustomTotalRows() {
        // UPDATED: Use unclosedShipmentData for row counts
        const dataLength = deliveries?.unclosedShipmentData?.data?.length || 0;
        const totalRows = deliveries?.unclosedShipmentData?.meta?.total || 0;
        return (
            <div className="custom-total-rows">
                <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
                    Showing {dataLength} of {totalRows}
                </FormControl>
            </div>
        )
    }

    function CustomPagesizeoptions() {
        return (
            <div className="custom-page-size-options">
                <span>
                    Show <select value={meta?.per_page} onChange={onChangePerPage}>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select> entries
                </span>
            </div>
        )
    }

    function CustomToolbar() {
        return (
            <GridToolbarContainer>
                <CustomPagesizeoptions />
                <CustomTotalRows />
            </GridToolbarContainer>
        );
    }

    function CustomPagination() {
        // UPDATED: Use unclosedShipmentData meta for pagination
        return (
            <Pagination
                color="primary"
                shape="circular"
                page={deliveries?.unclosedShipmentData?.meta?.current_page || 1}
                count={deliveries?.unclosedShipmentData?.meta?.last_page || 1}
                onChange={onChangePagination}
            />
        );
    }

    return (
        <Drawer
            anchor="right"
            open={visible}
            onClose={(event, reason) => {
                if (reason !== "backdropClick") ondrawerclose();
            }}
            PaperProps={{ sx: { width: drawerWidth } }}
        >
            {deliveries?.unclosedShipmentData?.notification?.show &&
                <Notification
                    is_open={deliveries.unclosedShipmentData.notification.show}
                    messageType={deliveries.unclosedShipmentData.notification.messageType}
                    messages={deliveries.unclosedShipmentData.notification.messageContent}
                    handleClose={() => dispatch(resetDeliveriessNotifications())}
                />}
            <div style={{ padding: "0 30px" }}>
                <Card sx={{ marginTop: "20px" }} className="sc--cardhead--details">
                    <div style={{ width: '100%', background: '#fff' }}>
                        <CardHeader title="Unclosed Shipment Details" action={
                            <div>
                                <WidthMaximize defaultWidth="80%" onToggle={setDrawerWidth} />
                                <IconButton onClick={ondrawerclose}><CloseIcon /></IconButton>
                            </div>
                        } />
                        <DataGrid
                            style={{ paddingTop: "37px" }}
                            getRowId={(row) => row._id}
                            // UPDATED: Data source from unclosedShipmentData
                            rows={deliveries?.unclosedShipmentData?.data || []}
                            columns={columns}
                            autoHeight
                            disableColumnFilter
                            disableColumnMenu
                            sortingMode="server"
                            onSortModelChange={onSortChange}
                            sortModel={meta?.sorting ? JSON.parse(meta.sorting) : []}
                            disableRowSelectionOnClick
                            slots={{
                                loadingOverlay: LinearProgress,
                                pagination: CustomPagination,
                                toolbar: CustomToolbar,
                                noRowsOverlay: NorowsData
                            }}
                            columnVisibilityModel={{
                                rate_total_charges: profile?.profile?.hide_rate !== true, 
                            }}
                            // UPDATED: Loading state from unclosedShipmentData
                            loading={deliveries?.unclosedShipmentData?.loading}
                        />
                    </div>
                </Card>
            </div>
            <TrackingDrawer visible={trackingDrawerVisible} isMultiPiece={isMultiPiece} onClose={() => setTrackingDrawerVisible(false)} />
            <OrderDrawer visible={orderDrawerVisible} onClose={orderOnClose} />
        </Drawer>
    )
}

export default UnclosedShipmentDrawer;